import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Webcam from "react-webcam";
import styled from "styled-components";
import { MainButton } from "../../core/components/buttons/MainButton";
import { TextButton } from "../../core/components/buttons/TextButton";
import { Body, H4 } from "../../core/styles/stringStyles";
import { useStyles } from "../../core/styles/theme";

type Props = {
  image: string | undefined;
  setImage: (image: string | undefined) => void;
  remoteImage: string | undefined;
};
export const DocumentCamera = ({ image, setImage, remoteImage }: Props) => {
  const theme = useStyles();
  const { t } = useTranslation();
  const webcamRef = useRef<Webcam>(null);
  const [permissionStatus, setPermissionStatus] = useState<
    "granted" | "denied" | "prompt" | "checking"
  >("checking");
  const [hasCamera, setHasCamera] = useState(true);
  const [isCapturing, setIsCapturing] = useState(false);
  const [showRemoteImage, setShowRemoteImage] = useState(Boolean(remoteImage));
  const handleUserMediaError = (error: string | DOMException) => {
    console.error("Camera error:", error);

    if (typeof error === "object" && error.name === "NotAllowedError") {
      setPermissionStatus("denied");
    } else if (typeof error === "object" && error.name === "NotFoundError") {
      setHasCamera(false);
    }
  };

  const captureScreenshot = async () => {
    if (image || (remoteImage && showRemoteImage)) {
      retryCapture();
      return;
    }
    setIsCapturing(true);
    try {
      const imageSrc = webcamRef.current?.getScreenshot();
      if (imageSrc) {
        setImage(imageSrc);
      }
    } catch (error) {
      console.error("Error capturing screenshot:", error);
    } finally {
      setIsCapturing(false);
    }
  };

  const retryCapture = () => {
    setImage(undefined);
    setShowRemoteImage(false);
  };

  const requestCameraPermission = () => {
    setPermissionStatus("checking");
    // This will trigger the webcam component to request permission again
    window.location.reload();
  };

  const isLoading = permissionStatus === "checking";
  const isPrompting = permissionStatus === "prompt";
  const isCameraReady = permissionStatus === "granted" && hasCamera;
  const isPermissionDenied = permissionStatus === "denied";
  const hasNoCameraDevice = !hasCamera && permissionStatus !== "checking";

  const showCamera = !image && (!remoteImage || !showRemoteImage);
  return (
    <CameraContainer>
      {remoteImage && showRemoteImage ? (
        <CapturedImage src={remoteImage} alt={t("CAMERA_PREVIOUS_PHOTO")} />
      ) : image ? (
        <CapturedImage src={image} alt={t("CAMERA_CAPTURED_PHOTO")} />
      ) : (
        <>
          <StyledWebcam
            ref={webcamRef}
            videoConstraints={{
              facingMode: { ideal: "environment" },
            }}
            onUserMedia={() => setPermissionStatus("granted")}
            onUserMediaError={handleUserMediaError}
            style={{ display: showCamera ? "block" : "none" }}
            screenshotQuality={1}
          />
          {/* Show status messages over the webcam */}
          {hasNoCameraDevice && (
            <StatusOverlay>
              <H4>{t("CAMERA_NO_CAMERA")}</H4>
              <Body>{t("CAMERA_NO_CAMERA_DESCRIPTION")}</Body>
            </StatusOverlay>
          )}
          {isPrompting && (
            <StatusOverlay>
              <H4>{t("CAMERA_ACCESS_REQUIRED")}</H4>
              <Body>{t("CAMERA_ACCESS_REQUIRED_DESCRIPTION")}</Body>
              <ButtonContainer>
                <TextButton
                  text={t("CAMERA_REQUEST_ACCESS")}
                  onClick={requestCameraPermission}
                  color={theme.colors.primary}
                />
              </ButtonContainer>
            </StatusOverlay>
          )}
          {isPermissionDenied && (
            <StatusOverlay>
              <H4>{t("CAMERA_ACCESS_DENIED")}</H4>
              <Body>{t("CAMERA_ACCESS_DENIED_DESCRIPTION")}</Body>
              <ButtonContainer>
                <TextButton
                  text={t("CAMERA_RELOAD_PAGE")}
                  onClick={requestCameraPermission}
                  color={theme.colors.primary}
                />
              </ButtonContainer>
            </StatusOverlay>
          )}
          {isLoading && (
            <StatusOverlay>
              <H4>{t("CAMERA_LOADING")}</H4>
              <Body>{t("CAMERA_LOADING_DESCRIPTION")}</Body>
            </StatusOverlay>
          )}
        </>
      )}

      {(isCameraReady || image || remoteImage) && (
        <CaptureButtonContainer>
          <MainButton
            text={
              !showCamera
                ? t("CAMERA_RETRY")
                : isCapturing
                ? t("CAMERA_CAPTURING")
                : t("CAMERA_TAKE_PHOTO")
            }
            onClick={captureScreenshot}
            disabled={!isCameraReady && showCamera}
            loading={isCapturing}
            color={theme.colors.primary}
          />
        </CaptureButtonContainer>
      )}
    </CameraContainer>
  );
};

const CameraContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  min-height: 400px;
  border-radius: 12px;
  overflow: hidden;
`;

const StatusOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px 20px;
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: 12px;
  text-align: center;
  z-index: 10;
  backdrop-filter: blur(4px);
`;

const StyledWebcam = styled(Webcam)`
  width: 100%;
  height: auto;
  display: block;
`;

const CapturedImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 12px;
  border: 2px solid ${({ theme }) => theme.colors.light};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 16px;
`;

const CaptureButtonContainer = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
`;
